import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'preact/hooks';
import Pubnub from 'pubnub';
import { usePubNub } from 'pubnub-react';

import { FeedMessageWithMeta } from './types';

const CHANNEL = 'communityUpdates';

const queryFn = async (
  pubnub: Pubnub | undefined,
  channelId: string,
  params: Omit<Pubnub.FetchMessagesParameters, 'channels'>,
) => {
  return new Promise<{ more: Pubnub.FetchMessagesResponse['more']; messages: FeedMessageWithMeta[] }>((resolve, reject) => {
    pubnub?.fetchMessages(
      {
        channels: [channelId],
        ...params,
        start: params.start ?? `${Date.now()}0000`,
      },
      (status, resp) => {
        if (status.error) {
          reject(status.errorData);
          return;
        }
        resolve({
          more: resp?.more,
          messages: resp?.channels?.[CHANNEL] ?? [],
        });
      },
    );
  });
};

export const getCommunityFeedQueryOptions = (pubnub: Pubnub | undefined, count: number) =>
  queryOptions({
    queryKey: [pubnub, 'messages', 'list', CHANNEL, { count }],
    queryFn: () => queryFn(pubnub, CHANNEL, { count }),
    enabled: !!pubnub,
    select: (data) => [...data.messages].reverse(),
    // data will be updated through pubnub listener
    staleTime: Infinity,
    refetchOnMount: 'always',
  });

export const getCommunityFeedInfiniteQueryOptions = (pubnub: Pubnub | undefined) =>
  infiniteQueryOptions({
    queryKey: [pubnub, 'messages', 'list', CHANNEL],
    queryFn: ({ pageParam }) => queryFn(pubnub, CHANNEL, { start: pageParam, count: 100, includeMessageActions: true }),
    enabled: !!pubnub,
    initialPageParam: '',
    getNextPageParam: (lastPage: Awaited<ReturnType<typeof queryFn>>) => lastPage?.more?.start,
    select: ({ pages }) => pages.flatMap((page) => [...page.messages].reverse()),
    // data will be updated through pubnub listener
    staleTime: Infinity,
    refetchOnMount: 'always',
  });

export const useCommunityFeedQuery = (initialCount: number) => {
  const pubnub = usePubNub();
  const queryClient = useQueryClient();
  const query = useQuery(getCommunityFeedQueryOptions(pubnub, initialCount));

  // subscribe to new messages and update the query data directly
  useEffect(() => {
    const handler: Pubnub.ListenerParameters['message'] = (event: FeedMessageWithMeta) => {
      if (event.channel !== CHANNEL) return;
      queryClient.setQueryData(getCommunityFeedQueryOptions(pubnub, initialCount).queryKey, (oldData) => {
        if (oldData?.messages.some((message) => message.timetoken === event.timetoken)) return oldData;
        return { more: oldData?.more, messages: (oldData?.messages ?? []).concat(event) };
      });
    };
    pubnub.addListener({ message: handler });
    pubnub.subscribe({ channels: [CHANNEL] });
    return () => {
      pubnub.unsubscribe({ channels: [CHANNEL] });
      pubnub.removeListener({ message: handler });
    };
  }, [initialCount, pubnub, queryClient]);

  return query;
};

export const useCommunityFeedInfiniteQuery = () => {
  const pubnub = usePubNub();
  const queryClient = useQueryClient();
  const query = useInfiniteQuery(getCommunityFeedInfiniteQueryOptions(pubnub));

  useEffect(() => {
    const handler: Pubnub.ListenerParameters['message'] = (event: FeedMessageWithMeta) => {
      if (event.channel !== CHANNEL) return;
      queryClient.setQueryData(getCommunityFeedInfiniteQueryOptions(pubnub).queryKey, (oldData) => {
        if (!oldData) return oldData;
        const firstPage = oldData.pages?.[0];
        if (!firstPage) return oldData;
        if (firstPage.messages.some((message) => message.timetoken === event.timetoken)) return oldData;
        const newData = { more: firstPage.more, messages: firstPage.messages.concat(event) };
        return {
          ...oldData,
          pages: [newData, ...oldData.pages.slice(1)],
        };
      });
    };
    pubnub.addListener({ message: handler });
    pubnub.subscribe({ channels: [CHANNEL] });
    return () => {
      pubnub.unsubscribe({ channels: [CHANNEL] });
      pubnub.removeListener({ message: handler });
    };
  }, [pubnub, queryClient]);

  return query;
};
